/**
 * §8's Settings page, plus 4e's Language row.
 *
 * The largest section in the catalogue, and the one where the words *are* the feature: core's probe
 * answers with finding ids and its capability record with `serves | disabled | unknown`, so every
 * sentence a reader can act on about their instance is here rather than in core (R7). Keeping them
 * together is what makes them reviewable as copy instead of as scattered JSX.
 *
 * This page is also the one place where a fact about the instance is *the subject* rather than
 * background noise: somebody who types an address into the box and presses Check is asking a technical
 * question and deserves a technical answer. Everything else is still held to the same rule as the rest
 * of the app — a setting says what it changes for the reader, and what a write or a sync did to their
 * data, and stops before saying how.
 */
export const settings = {
  heading: 'Settings',
  lede:
    'Where your instance has a matching setting, your account’s value is what you see here and changes are saved straight back to it. The rest lives in this browser. The small icon after each name says which is which.',

  /** The storage icons' hover titles — the whole story in one line, since the icon says the rest. */
  storage: {
    account: 'Follows your account when you are signed in.',
    browser: 'Stays in this browser.',
  },

  /** The four topic tabs — path segments, so these are destinations rather than headings. */
  tab: {
    playback: 'Playback',
    interface: 'Interface',
    integrations: 'Integrations',
    instance: 'Instance',
    data: 'Data',
  },
  tabsLabel: 'Settings sections',

  /** §3's four resolution steps, in the words a reader can act on. */
  instanceSource: {
    'url-param': 'from the address bar, for this page load only',
    'saved-setting': 'saved in this browser',
    'config-json': 'the default for this site',
    'build-default': 'the built-in fallback',
  },

  instanceUrlProblem: {
    empty: 'Enter the address of an Invidious instance.',
    notAbsolute: 'Include the scheme: https://invidious.example.com, not invidious.example.com.',
    unsupportedScheme: 'Only http:// and https:// can be used.',
    hasQuery: 'Give the address only, with nothing after it.',
  },

  instance: {
    resolving: 'Resolving…',
    connectedTo: 'Connected to',
    addressLabel: 'Instance address',
    addressPlaceholder: 'https://invidious.example.com',
    check: 'Check',
    checking: 'Checking…',
    switch: 'Switch',
    running: 'Running the five checks…',
    checkFailed: 'The check could not be completed.',
    saveRefused: 'This browser would not save the instance, so it cannot be switched.',
    clearRefused: 'This browser would not clear the saved instance.',
    usable: 'A video loads, so this instance can serve the app.',
    unusable:
      'The test video did not load, so this instance cannot serve the app. That check is the one that decides. Nothing else below changes it.',
    stepSkipped: 'skipped',
    stepNoAnswer: 'no answer',
    software: 'Software',
    useDefault: 'Use the default again',
    /** The Invidious page Visious links to rather than rebuilds (`instancePreferencesLink`). */
    openPreferences: 'Open your instance’s own settings',
    openPreferencesDetail:
      'Instance-level preferences Visious has no control over, on the instance’s own page.',
    /**
     * What switching costs, spelled out because R9 makes it destructive in a way nothing on screen
     * would otherwise show: `clearOtherInstances()` runs on every boot and drops every stored session
     * that is not the current instance's, so switching away *ends* the old one rather than parking it.
     * "You will be signed out" would be the comfortable half of that sentence.
     */
    switchWarning:
      'Accounts are per instance, including associated subscriptions, playlists, history, and some settings. Switching will sign you out and remove this data from Visious.',
  },

  probeStep: {
    stats: '1. Check if it is Invidious, and which version',
    'public-api': '2. Check if the API is answering',
    video: '3. Check if a video loads',
    features: '4. Check what the instance offers',
    captions: '5. Check if subtitles arrive',
  },

  /**
   * One sentence per finding id. Every one is a measured state rather than a category, and this is the
   * page where naming the mechanism is the point: a reader who ran the check is diagnosing an instance,
   * and "it did not work" would not tell them which end to fix.
   */
  probeFinding: {
    unreachable: 'No answer at all. The host is down, or the address is wrong.',
    corsBlocked:
      'The instance answered, but not to this page: its CORS settings do not allow the address you are on. That is a setting on the instance, not something Visious can work around.',
    aborted: 'The check was cancelled.',
    httpError: 'It answered with an error instead of an answer.',
    emptyBody: 'It answered with nothing at all.',
    notJson: 'What answered does not look like Invidious. It is usually a web server or a proxy in front of it.',
    notInvidious: 'Whatever is running there does not identify itself as Invidious.',
    noAdaptiveFormats: 'The test video offers no choice of quality.',
    noDashManifest: 'The test video has no adaptive stream.',
    manifestUnreadable:
      'The test video’s stream could not be read. Worth trying again before believing it, since this instance gets a few per cent of these wrong at random.',
    noCaptionsListed: 'The test video has no subtitles, so this could not be checked.',
    popularDisabled: 'The administrator has switched Popular off. Home falls back to Trending.',
    authAbsent:
      'This instance has no accounts, so signing in, subscriptions, playlists and history are unavailable.',
    captionsDocumentedPath:
      'Subtitles arrive and can be shown, by a route most instances answer empty. Unusual, and fine.',
    captionsUnavailable: 'No subtitles arrived, so they will be hidden.',
  },

  capability: {
    serves: 'Yes',
    disabled: 'No',
    unknown: 'Not known',
    popular: 'Popular feed',
    popularDisabled: 'Home will show Trending instead.',
    trending: 'Trending feed',
    adaptiveFormats: 'Choice of quality',
    dashManifest: 'Adaptive streaming',
    auth: 'Accounts and sign-in',
    captions: 'Subtitles',
    captionsHidden: 'The subtitles button will be hidden.',
  },

  /**
   * One row per control: `label` is the row's title, `detail` the one line under it, and
   * `detailTitle` — where present — the longer explanation, shown as the row's hover title because
   * the line under a title is owed exactly one line (the layout rule).
   */
  control: {
    quality: {
      label: 'Default quality',
      hint: 'If a video does not have this quality, the nearest one it does have plays instead.',
      auto: 'Adaptive (recommended)',
      height: (height: number): string => `${height}p`,
    },
    speed: { label: 'Default speed', rate: (rate: number): string => `${rate}×` },
    audioLanguage: {
      label: 'Preferred audio language',
      detail: 'Videos with more than one soundtrack start in this language.',
      /** §7's `auto` — the language the video was recorded in. */
      original: 'Original',
    },
    subtitleLanguage: {
      label: 'Preferred subtitles',
      off: 'Off',
      detail: 'Subtitles turn on in this language when a video has them.',
    },
    youtubeEmbed: {
      label: 'Switch to YouTube',
      detail: 'Adds a way to replace the player with an embedded YouTube video.',
      detailTitle:
        'Adds a YouTube entry to the Switch player section of the watch page’s Watch on menu. Pressing it replaces the player with YouTube’s own embedded one, chrome and all, at the same point in the video, with a way back to Invidious in its place.',
    },
    autoplay: {
      label: 'Start playing on arrival',
      detail: 'The browser has the last word and may start muted instead.',
      detailTitle:
        'Chrome blocks playback with sound until you have interacted with the site, and Visious then starts muted instead of pretending it worked.',
    },
    useStableVolume: {
      label: 'Use stable volume audio tracks',
      detail: 'Plays the evened-out version of the soundtrack where a video has one.',
      detailTitle:
        'YouTube prepares a version of some soundtracks with the loudness evened out. The player’s audio menu has the same switch, and flipping it there changes this setting too.',
    },
    forceAvc: {
      label: 'Force H.264',
      detail: 'For hardware that cannot decode AV1.',
      detailTitle: 'Takes effect on the next video you open.',
    },
    resumePlayback: {
      label: 'Resume where you left off',
      detail: 'Opening a video continues where you stopped, and thumbnails show how far you got.',
      detailTitle:
        'A timestamp in a link always wins over the position you stopped at.',
    },
    region: {
      label: 'Content region',
      hint: 'What Trending shows. With no preference your instance uses its own.',
      none: 'No preference',
    },
    landing: {
      label: 'Landing page',
      hint: 'Where opening Visious takes you. The brand link in the header goes there too.',
      popular: 'Popular',
      subscriptions: 'Subscriptions',
      playlists: 'Playlists',
      history: 'History',
    },
    /**
     * The row under Menu sections. Where the Trending screen went: its heading, its search and
     * its grid are a section on the popular page now, and this is the only thing that puts them there.
     */
    trendingOnPopular: {
      label: 'Trending on the popular page',
      detail: 'Adds a Trending section above the popular videos.',
      detailTitle:
        'The Trending list has no page of its own. Turning this on puts it at the top of the popular page, with its own search.',
    },
    /** The Menu row — the four show/hide choices folded into one multi-select control. */
    menu: {
      label: 'Menu sections',
      detail: 'Hiding one only tidies the menu. Its page keeps working.',
      detailTitle:
        'Which sections the menu shows. A hidden section’s page keeps working, and the landing page can still point at it.',
      allShown: 'All shown',
      shownOf: (shown: number, total: number): string => `${shown} of ${total} shown`,
      popularDisabled:
        'Your instance has the popular feed switched off, so there is nothing for this item to show.',
    },
    theme: {
      label: 'Theme',
      hint: 'Following the system means it changes when your desktop does, without a reload.',
      system: 'Follow the system',
      dark: 'Dark',
      light: 'Light',
    },
    gridPreviews: {
      label: 'Video previews',
      detail: 'Resting the pointer on a thumbnail previews the video, and the bar below scrubs it.',
      detailTitle:
        'Resting the pointer on a video card’s thumbnail for a moment flips through the video’s preview frames. Moving the pointer along the bar along the bottom edge scrubs the preview.',
    },
    /** The corner player. On by default, so the detail line describes what *off* costs. */
    miniPlayer: {
      label: 'Keep playing when you leave the video',
      detail: 'The video moves to a small player in the corner instead of stopping.',
      detailTitle:
        'Leaving the watch page while a video is playing keeps it in a small player, in the bottom corner or as a strip above the menu on a narrow screen.',
    },
    watchLater: {
      label: 'Watch later playlist',
      hint: 'The playlist the Watch later buttons save into.',
      hintTitle:
        'The Watch later buttons on video cards and the watch page save into this playlist.',
      none: 'None chosen',
      /** The control's face while signed out — the picker explains and links to sign-in. */
      choose: 'Choose…',
    },
    mirrorHistory: {
      label: 'Record what you watch to your account',
      detail: 'Off means nothing you watch is marked on the instance.',
      detailTitle:
        'Off means nothing you watch is ever remembered on the instance, and the History page shows only what is already there.',
    },
    /**
     * **4e.** Three options today, one per catalogue plus "follow the system"; the list grows with `UI_LOCALES`, and
     * the compiler is what enforces that a language on offer is one there is copy for.
     *
     * The hint says which language is *active* and where the choice came from, because "follow the
     * system" on a system asking for German resolves to English and a reader deserves to know why.
     */
    language: {
      label: 'Language',
      followSystem: 'Follow the system',
      /**
       * One key per `UiLocaleId`, and the compiler is what keeps it that way: `uiLanguageOptions` indexes
       * this table with the id, so a locale added to `UI_LOCALES` without a name here does not build.
       * These are the names in *this* catalogue's language, because they are read inside its sentences
       * ("The interface is in German") and not only as options in a list.
       */
      en: 'English',
      de: 'German',
      hintSetting: (language: string): string => `The interface is in ${language}, because you chose it.`,
      hintSystem: (language: string): string =>
        `${language} is the first language on your system's list that Visious has, so that is what the interface is in.`,
      hintFallback: (language: string): string =>
        `Your system asks for a language Visious has no copy of yet, so the interface is in ${language}.`,
      formatting: (locale: string): string => `Dates and numbers use ${locale}.`,
    },
  },

  /**
   * The Integrations tab's one card pair so far. The category names themselves live in the shared
   * `sponsorBlock` section, because the Watch page speaks them too (badges, toasts).
   */
  sponsorBlock: {
    title: 'SponsorBlock',
    lede:
      'Segments other viewers have marked in videos: sponsor reads, intros, reminders to subscribe. Visious can draw them on the progress bar, badge chapters they fill, and skip them. Lookups are anonymous, because the service is sent a hash, never the video itself.',
    enable: {
      label: 'Use SponsorBlock',
      detail: 'Look up segments for every video you open.',
    },
    address: {
      label: 'Service address',
      detail: 'Leave empty for the official service, sponsor.ajay.app.',
      /** The instance field's sentence, with an example that is not an Invidious address. */
      notAbsolute: 'Include the scheme: https://sponsor.example.com, not sponsor.example.com.',
    },
    behavior: {
      auto: 'Skip automatically',
      toast: 'Offer a skip button',
      off: 'Do nothing',
    },
    categories: {
      title: 'Categories',
      lede:
        'What to do about each kind of segment, and where it shows: a stripe on the progress bar in the category color, and a badge on a chapter a segment fills at least three quarters of.',
      /** The swatch's accessible name — the menu beside it is named by the category itself. */
      colorLabel: (category: string): string => `${category} color`,
      /** The two sections of a category's menu: one-of-these, then any-of-these. */
      behaviorTitle: 'Skip behavior',
      showTitle: 'Show on',
      bar: 'Progress bar',
      chapters: 'Chapters',
    },
  },

  /** The DeArrow card on the same tab (2026-08-02). */
  deArrow: {
    title: 'DeArrow',
    lede:
      'Community-written titles and thumbnails that describe a video instead of selling it, shown on video cards everywhere. A video you are already watching keeps its real title. Title lookups are anonymous, with a hash sent to the service instead of the video itself.',
    enable: {
      label: 'Use DeArrow',
      detail: 'Replace titles on video cards where the community has written one.',
    },
    thumbnails: {
      label: 'Replace thumbnails',
      detail:
        'Also swap in community-chosen video frames. Unlike title lookups, fetching these pictures sends the plain video id to the thumbnail service.',
    },
    address: {
      label: 'Service address',
      detail: 'Leave empty for the official service, sponsor.ajay.app.',
    },
    thumbnailAddress: {
      label: 'Thumbnail service address',
      detail: 'Leave empty for the official service, dearrow-thumb.ajay.app.',
    },
  },

  /** The Return YouTube Dislike card on the same tab — SponsorBlock's simplest shape. */
  returnYoutubeDislike: {
    title: 'Return YouTube Dislike',
    lede:
      'An estimated dislike count, shown beside the like count on the watch page. YouTube stopped showing the real figure publicly, so this is a community project’s estimate instead. A lookup sends the plain video id to the service.',
    enable: {
      label: 'Use Return YouTube Dislike',
      detail: 'Look up an estimated dislike count for every video you open.',
    },
    address: {
      label: 'Service address',
      detail: 'Leave empty for the official service, returnyoutubedislikeapi.com.',
    },
  },

  /** The PeerTube card on the same tab — the fallback player named long ago and never built until now. */
  peerTube: {
    title: 'PeerTube',
    lede:
      'A fallback for when a video won’t play here. It searches for a matching upload by title and length, and offers a “PeerTube” entry in both sections of the watch page’s Watch on menu. Under “Switch player” it plays the match here in place of the video, and under “Open site” it opens the match on its own instance.',
    enable: {
      label: 'Use PeerTube',
      detail: 'Search for a matching video whenever the Watch on menu opens.',
    },
    address: {
      label: 'Search instance',
      detail:
        'Leave empty for the default, sepiasearch.org.',
    },
    searchTarget: {
      label: 'Search target',
      detail: 'Which video sources the search instance is asked about.',
      searchIndex: 'Connected instances',
      local: 'Local only',
    },
    mode: {
      label: 'Player',
      detail: 'How a matched video plays. The integrated player, or PeerTube’s own embedded one.',
      integrated: 'Integrated',
      embedded: 'Embedded',
    },
  },

  /** The short language list for the two *track* menus — see `model.ts` for why it is not a claim. */
  language: {
    ar: 'Arabic',
    bn: 'Bangla',
    de: 'German',
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    hi: 'Hindi',
    id: 'Indonesian',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    nl: 'Dutch',
    pl: 'Polish',
    pt: 'Portuguese',
    ru: 'Russian',
    tr: 'Turkish',
    vi: 'Vietnamese',
    zhHans: 'Chinese (Simplified)',
  },

  /**
   * The box reports a *state*, so it is headed for the question it answers and the state is the
   * loudest thing in it. `status` is the reading, two or three words, and `detail` the line under it
   * that says what the reading means for the reader's settings.
   */
  syncState: {
    title: 'Settings sync',
    signedOut: {
      status: 'Not syncing',
      detail:
        'Every setting is saved in the browser only right now. Sign in to your instance and sync matching settings.',
    },
    syncing: {
      status: 'Checking your account',
      detail:
        'Your account is being checked right now. Once that\'s done matching settings will be synced.',
    },
    synced: {
      status: 'In sync',
      detail:
        'Matching account settings are being synced to your instance.',
    },
  },

  account: {
    /** 4e's third named victim of the mixed-locale sentence — see `playlists.page.updated`. */
    lastSynced: (when: string): string => `Last synced ${when}.`,
  },

  /**
   * The Data tab. `importExport` is the `/data_control` link under the two recording switches —
   * moved here from the Instance tab, since the reader looking for their data looks on Data. The
   * clear card: a heading, one line of buttons each named for the store it empties, and the four
   * stores explained below the line rather than per button (`explain`) — term over text, each
   * naming an example or two of what lives there, what clearing costs, and whether it comes back.
   */
  data: {
    importExport: {
      label: 'Import or export your data',
      detail: 'Subscriptions, playlists and history, moved in or out through the instance’s own tools.',
    },
    clearTitle: 'Clear data',
    clearLede: 'Each button asks once more before anything is deleted.',
    history: {
      label: 'Clear watch history',
      confirmTitle: 'Deletes the watch history and the stored positions for good. There is no undo.',
    },
    state: {
      label: 'Clear local state',
      confirmTitle: 'Resets this browser’s settings and page arrangements.',
    },
    cache: {
      label: 'Clear local cache',
      confirmTitle: 'Clears remembered names and cached copies. They refill themselves.',
    },
    auth: {
      label: 'Clear local auth',
      confirmTitle: 'Deletes the stored session. Signing back in takes two clicks.',
    },
    all: {
      label: 'Clear all',
      confirmTitle: 'Clears everything listed here: history, state, cache and the sign-in.',
    },
    explain: {
      history: {
        term: 'Watch history',
        text: 'What you have watched and where you stopped. The list of watched videos lives on your account, and the positions that let a video resume live in this browser. Clearing deletes both for good. Subscriptions, playlists and settings stay.',
      },
      state: {
        term: 'Local state',
        text: 'Choices only this browser remembers, like hidden menu sections or the watch page’s layout. Clearing puts them back to the defaults.',
      },
      cache: {
        term: 'Local cache',
        text: 'Copies kept so pages appear at once, like channel and video names or your account’s settings. Clearing loses nothing of yours. Everything here refills itself.',
      },
      auth: {
        term: 'Local auth',
        text: 'The token that keeps you signed in here. Clearing it signs this browser out and nothing more.',
      },
    },
  },

  preferences: {
    /** A write with one wrong value is discarded whole and reported as a success, so it is not sent. */
    unwritable: {
      title: 'Visious did not send this change',
      detail:
        'One of the values would not have been stored, and the instance would have reported success anyway, so it would have looked saved and would not have been. Nothing was changed. This is a bug here, not anything you did.',
      retryable: false,
    },
    notFound: {
      title: 'This instance cannot store settings',
      detail: 'There is nothing to sync with. Your settings still work, they just stay in this browser.',
      retryable: false,
    },
    unreadable: {
      title: 'Your account’s settings could not be read',
      detail: 'The instance answered with something Visious could not read. Your local settings are unaffected.',
      retryable: true,
    },
  },
} as const;
